"use client";

import { useMemo } from "react";

import { motion } from "motion/react";
import { useRouter } from "next/navigation";

import { Button } from "@/components/_ui/button";
import { Card, CardContent } from "@/components/_ui/card";
import { PublicTrackingDashboard } from "@/components/public-tracking/dashboard/PublicTrackingDashboard";
import { PublicTrackingLookup } from "@/components/public-tracking/PublicTrackingLookup";
import { trackingEventStops } from "@/utils/publicTracking/scanJourney";
import { usePublicTracking } from "@/utils/publicTracking/usePublicTracking";
import { useScanJourneyMap } from "@/utils/publicTracking/useScanJourneyMap";

interface PublicMailTrackingFormProps {
  /** Prefill the IMb field (e.g. from a `?imb=` deep link / QR code). */
  initialImb?: string;
  /** When true and `initialImb` is a valid IMb, run the lookup once on mount. */
  autoLookup?: boolean;
}

/**
 * Public, recipient-facing IMb lookup. When the SWR mutation has data,
 * renders the full `PublicTrackingDashboard` with a "Track another piece"
 * link; otherwise renders the inline `PublicTrackingLookup` card. The
 * header's "New IMb" button also opens the same lookup form in a popover.
 */
export function PublicMailTrackingForm({
  initialImb,
  autoLookup = false,
}: PublicMailTrackingFormProps) {
  const router = useRouter();
  const { data, reset } = usePublicTracking();

  const stops = useMemo(() => (data ? trackingEventStops(data) : []), [data]);
  const journey = useScanJourneyMap(stops);

  function handleTrackAnother() {
    reset();
    router.replace("/track");
  }

  if (data) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="space-y-6"
      >
        <PublicTrackingDashboard data={data} journey={journey} />
        <div className="flex justify-center">
          <Button
            variant="link"
            type="button"
            onClick={handleTrackAnother}
            className="text-muted-foreground hover:text-foreground h-auto p-0"
          >
            Track another piece
          </Button>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="mx-auto max-w-md">
      <Card>
        <CardContent>
          <PublicTrackingLookup
            initialImb={initialImb}
            autoLookup={autoLookup}
          />
        </CardContent>
      </Card>
    </div>
  );
}
